"use client";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { getUTMs } from "@/app/_analytics/utmHandler";
import { useUser } from "@/app/_providers/user";
import { Heading } from "@components/core/Heading";
import Spinner20 from "@ui/icons/20x20/spinner-20.svg";
import { cn } from "@ui/lib/utils";
import { useTranslations } from "next-intl";
import { usePostHog } from "posthog-js/react";
import { memo, useCallback, useEffect, useRef } from "react";

import type { ErrorContext, FeatherySDK } from "./FeatheryContext";

import { FeatheryProvider, useFeathery } from "./FeatheryContext";

type SubmitField = { value: string };

type FormContext = {
  destroy: () => void;
  setFieldValues: (values: unknown) => void;
  submitFields: { [key: string]: SubmitField };
} | null;

export const FeatheryFormRenderer = memo(
  ({
    formId,
    onSubmit,
    onLoad,
    title,
  }: {
    formId: string;
    onLoad?: (sdk: FeatherySDK) => void;
    onSubmit?: (submittedData: unknown) => void;
    title?: string;
  }) => {
    const { status, sdk } = useFeathery();
    const contextRef = useRef<FormContext>(null);
    const posthog = usePostHog();
    const { userData } = useUser();
    const t = useTranslations("ClientKeys.feathery_form");
    useEffect(() => {
      if (status !== "ready" || !posthog) return;
      if (contextRef.current) return;

      // Setup hidden tracking values
      const distinctId = posthog?.get_distinct_id() || "";
      const userId = userData?.user_id || "";
      const accountEmail = userData?.email || "";
      const utms = getUTMs();

      const hiddenFields = {
        user_id: userId,
        distinct_id: distinctId,
        account_email: accountEmail,
        utm_source: utms.utmSource,
        utm_medium: utms.utmMedium,
        utm_campaign: utms.utmCampaign,
        utm_content: utms.utmContent,
        utm_term: utms.utmTerm,
        gclid: utms.gclid,
      };

      // define event handlers
      const handleSubmission = (context: FormContext) => {
        const submittedData = context?.submitFields;
        const eventData: {
          [key: string]: null | string | undefined;
        } = {};

        if (submittedData) {
          Object.keys(submittedData).forEach(key => {
            const cleanKey = key.toLowerCase();
            eventData[cleanKey] = submittedData[key].value;
          });
        }

        eventData.form_name = title;
        eventData.form_id = formId;
        eventData.user_id = hiddenFields.user_id;
        eventData.account_email = hiddenFields.account_email;
        eventData.distinct_id = hiddenFields.distinct_id;
        eventData.utm_source = hiddenFields.utm_source;
        eventData.utm_medium = hiddenFields.utm_medium;
        eventData.utm_campaign = hiddenFields.utm_campaign;
        eventData.utm_content = hiddenFields.utm_content;
        eventData.utm_term = hiddenFields.utm_term;
        eventData.gclid = hiddenFields.gclid;

        captureEvent(EventName.feathery_form_submitted, eventData);

        // Only track v1 contact sales submissions for the experiment comparison
        if (formId === "pes4d4") {
          captureEvent(EventName.mw_contact_sales_v2_submission_succeeded, { ...eventData, csf_form_version: "v1" });
        }

        onSubmit?.(submittedData);
      };

      const handleInputChange = () => {
        captureEvent(EventName.feathery_form_information_entered, {
          form_name: title,
          form_id: formId,
          user_id: hiddenFields.user_id,
          account_email: hiddenFields.account_email,
          distinct_id: hiddenFields.distinct_id,
          utm_source: hiddenFields.utm_source,
          utm_medium: hiddenFields.utm_medium,
          utm_campaign: hiddenFields.utm_campaign,
          utm_content: hiddenFields.utm_content,
          utm_term: hiddenFields.utm_term,
          gclid: hiddenFields.gclid,
        });
      };

      const handleOnLoad = () => {
        onLoad?.(sdk);
      };

      const handleError = (context: ErrorContext) => {
        captureEvent(EventName.mw_contact_sales_feathery_error, {
          form_name: title,
          form_id: formId,
          error_message: context?.errorMessage,
          error_field_id: context?.errorFieldId,
          error_field_type: context?.errorFieldType,
          error_trigger_type: context?.trigger?.type,
        });
      };

      contextRef.current = sdk.renderAt(formId, {
        formId,
        onSubmit: handleSubmission,
        onLoad: handleOnLoad,
        onChange: handleInputChange,
        onError: handleError,
      });
    }, [formId, onSubmit, onLoad, status, posthog, title, contextRef, sdk, userData]);

    if (status === "error") {
      return <div className='color-black flex justify-center py-32'>{t("error_loading")}</div>;
    }

    return (
      <>
        <div className='feathery-form' id={formId}>
          {/* For some reason triggering any state update in this component causes the form to not work. Even though custom
          form is memo'ed. So instead we pass the loading spinner down and place it inside the div feathery will replace
          to add the form. Once the form is loaded it will be replaced with the form */}
          <div className='color-black flex justify-center py-32'>
            <Spinner20 className='color-black' />
          </div>
        </div>
      </>
    );
  },
);

FeatheryFormRenderer.displayName = "FeatheryFormRenderer";

export const FeatheryForm = ({
  title,
  formId,
  fullWidth = false,
  onSubmit,
  onLoad,
}: {
  formId: string;
  fullWidth: boolean;
  onLoad?: (sdk: FeatherySDK) => void;
  onSubmit?: (submittedData: unknown) => void;
  title?: null | string;
}) => {
  const handleOnLoad = useCallback(
    (sdk: FeatherySDK) => {
      onLoad?.(sdk);
    },
    [onLoad],
  );
  const handleOnSubmit = useCallback(
    (submittedData: unknown) => {
      onSubmit?.(submittedData);
    },
    [onSubmit],
  );
  return (
    <FeatheryProvider>
      <div className={cn(!fullWidth && "grid-layout")}>
        <div className={cn(!fullWidth && "grid-col-span-12 lg:grid-col-span-6 lg:col-start-4")}>
          {title && (
            <Heading className='f-heading-03 mb-40 text-center' level={2}>
              {title}
            </Heading>
          )}
          <div>
            <FeatheryFormRenderer formId={formId} onLoad={handleOnLoad} onSubmit={handleOnSubmit} />
          </div>
        </div>
      </div>
    </FeatheryProvider>
  );
};

export default memo(FeatheryForm);
